/**
 * 共通ボタン。primary / secondary / ghost / danger の 4 variant + sm/md/lg サイズ。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web の共通ボタンコンポーネント
 * @graph-connects none
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business variant ごとの Tailwind クラス文字列マップ。primary/secondary/ghost/danger の背景・テキスト・シャドウを定義
 * @graph-connects none
 */
const VARIANT_CLASSES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] active:bg-[var(--color-accent-active)] text-[var(--color-bg-base)] shadow-[0_8px_24px_-8px_var(--color-accent-glow)]',
  secondary:
    'bg-[var(--color-bg-elevated)] hover:bg-[var(--color-bg-hover)] active:bg-[var(--color-bg-active)] text-[var(--color-text-primary)] ring-1 ring-[var(--color-border)] hover:ring-[var(--color-border-strong)]',
  ghost:
    'bg-transparent hover:bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
  danger:
    'bg-[var(--color-danger)] hover:opacity-90 active:opacity-80 text-white shadow-[0_8px_24px_-8px_rgba(248,113,113,0.4)]',
};

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business サイズ (sm/md/lg) ごとの高さ・横パディング・文字サイズ・アイコン間隔の Tailwind クラス文字列マップ
 * @graph-connects none
 */
const SIZE_CLASSES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-9 px-3.5 text-sm gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-13 px-7 text-base gap-2.5',
};

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 共通ボタン本体。アイコンスロット・loading state・fullWidth に対応
 * @graph-connects none
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    disabled,
    className = '',
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = (disabled ?? false) || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={[
        'inline-flex items-center justify-center font-semibold rounded-[var(--radius-lg)]',
        'transition-all duration-[var(--duration-base)] ease-[var(--ease-out)]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'select-none',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"
        />
      ) : leftIcon ? (
        <span aria-hidden>{leftIcon}</span>
      ) : null}
      {children}
      {!loading && rightIcon ? <span aria-hidden>{rightIcon}</span> : null}
    </button>
  );
});
